/**
 * VendorReportedAmountsTables - Reusable component for displaying vendor-reported transaction data
 *
 * Shows:
 * - Rebate invoices table with file information
 * - Transaction volume by division (monthly breakdown)
 * - Transaction volume by product group (monthly breakdown)
 *
 * This component can be used in both:
 * - VendorReportedAmountsSection (agreement detail page)
 * - SupplierReportedAmountsPage (supplier invoices page)
 */

import React, { useMemo, useCallback, useState } from 'react';
import {
  FolderIcon,
  ReceiptIcon,
  FileTextIcon,
  TrashIcon,
  FileIcon,
  CircleNotchIcon,
  DownloadIcon,
  ArrowUpIcon,
  ArrowDownIcon,
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader } from 'src/components/ui/card';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import { Switch } from 'src/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import GentlyTable from 'src/components/common/GentlyTable';
import SupplierPill from '@/components/company/SupplierPill';
import { formatPaymentDate } from 'src/utils/dateUtils';
import { formatCurrency } from '@erp/shared-utils';
import type { RebateInvoice } from '@/api/rebates/types';
import type { RebateLineItem } from '@/api/rebates/useRebateLineItems';
import { InlineEditableInvoiceNumber } from '@/components/rebates/InlineEditableInvoiceNumber';
import { useUpdateRebateInvoice } from '@/api/rebates/useUpdateRebateInvoice';

// Generic line item interface that works with both RebateLineItem and EnrichedLineItem
interface BaseLineItem {
  date: string;
  divisionId: string;
  divisionName: string | null;
  productGroupId: string | null;
  productGroupName: string | null;
  value: number;
}

interface VendorReportedAmountsTablesProps {
  // Invoices data
  invoices?: RebateInvoice[];
  invoicesLoading?: boolean;

  // Line items data
  lineItems?: BaseLineItem[];
  lineItemsLoading?: boolean;

  // Cumulative toggle
  isCumulative?: boolean;

  // Filters
  selectedDivisionIds?: Set<string>;
  showNonRebateable?: boolean;
  onShowNonRebateableChange?: (show: boolean) => void;

  // Invoice handlers
  onViewDocument?: (documentId: string, fileName?: string) => void;
  onDeleteInvoice?: (invoiceId: string) => void;

  // Optional: hide invoices table
  hideInvoicesTable?: boolean;
}

export function VendorReportedAmountsTables({
  invoices,
  invoicesLoading = false,
  lineItems,
  lineItemsLoading = false,
  isCumulative = false,
  selectedDivisionIds,
  showNonRebateable: controlledShowNonRebateable,
  onShowNonRebateableChange,
  onViewDocument,
  onDeleteInvoice,
  hideInvoicesTable = false,
}: VendorReportedAmountsTablesProps) {
  // Internal state for showNonRebateable if not controlled
  const [internalShowNonRebateable, setInternalShowNonRebateable] =
    useState(true);
  const showNonRebateable =
    controlledShowNonRebateable ?? internalShowNonRebateable;
  const setShowNonRebateable =
    onShowNonRebateableChange ?? setInternalShowNonRebateable;

  // Hook for updating invoice number
  const updateInvoiceMutation = useUpdateRebateInvoice();

  // Sort state for division table - default to alphabetical by division name
  const [divisionSortColumn, setDivisionSortColumn] = useState<string | null>(
    'division',
  );
  const [divisionSortDirection, setDivisionSortDirection] = useState<
    'asc' | 'desc'
  >('asc');

  // Sort state for product group table - default to alphabetical by product group name
  const [productGroupSortColumn, setProductGroupSortColumn] = useState<
    string | null
  >('productGroup');
  const [productGroupSortDirection, setProductGroupSortDirection] = useState<
    'asc' | 'desc'
  >('asc');

  // Helper to generate all months between two month keys (inclusive)
  const generateMonthRange = useCallback(
    (startMonthKey: string, endMonthKey: string): string[] => {
      const result: string[] = [];
      const [startYear, startMonth] = startMonthKey.split('-').map(Number);
      const [endYear, endMonth] = endMonthKey.split('-').map(Number);

      let currentYear = startYear;
      let currentMonth = startMonth;

      while (
        currentYear < endYear ||
        (currentYear === endYear && currentMonth <= endMonth)
      ) {
        result.push(`${currentYear}-${String(currentMonth).padStart(2, '0')}`);
        currentMonth++;
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear++;
        }
      }

      return result;
    },
    [],
  );

  // Helper to get all months from items
  const getAllMonths = useCallback(
    (items: { date: string }[]) => {
      if (!items || items.length === 0) return [];

      // Get all unique months from items
      // Use UTC methods to parse dates consistently
      const monthSet = new Set<string>();
      items.forEach((item) => {
        const itemDate = new Date(item.date);
        const monthKey = `${itemDate.getUTCFullYear()}-${String(itemDate.getUTCMonth() + 1).padStart(2, '0')}`;
        monthSet.add(monthKey);
      });

      const allMonths = Array.from(monthSet).sort();

      // In cumulative mode, show all months from first to last (filling gaps)
      if (isCumulative && allMonths.length > 0) {
        return generateMonthRange(
          allMonths[0],
          allMonths[allMonths.length - 1],
        );
      }
      return allMonths;
    },
    [isCumulative, generateMonthRange],
  );

  // Filter line items based on selected divisions and non-rebateable toggle
  const filteredLineItems = useMemo(() => {
    if (!lineItems) return undefined;

    let result = lineItems;

    // Filter out non-rebateable items if toggle is off
    if (!showNonRebateable) {
      result = result.filter((item) => item.productGroupId !== null);
    }

    // Filter to only selected divisions (if any selected)
    if (selectedDivisionIds && selectedDivisionIds.size > 0) {
      result = result.filter((item) =>
        selectedDivisionIds.has(item.divisionId),
      );
    }

    return result;
  }, [lineItems, selectedDivisionIds, showNonRebateable]);

  // Calculate volume breakdown per division by month
  const volumesByDivision = useMemo(() => {
    if (!filteredLineItems) {
      return { months: [], rows: [] };
    }

    const months = getAllMonths(filteredLineItems);

    // Map: divisionId -> { divisionName, monthValues: Map<monthKey, value> }
    const divisionMap = new Map<
      string,
      {
        divisionId: string;
        divisionName: string;
        monthValues: Map<string, number>;
        total: number;
      }
    >();

    filteredLineItems.forEach((item) => {
      const itemDate = new Date(item.date);
      // Use UTC methods to parse dates consistently (avoid timezone issues)
      const monthKey = `${itemDate.getUTCFullYear()}-${String(itemDate.getUTCMonth() + 1).padStart(2, '0')}`;

      // Only include if month is in our filter
      if (!months.includes(monthKey)) return;

      const existing = divisionMap.get(item.divisionId);
      if (existing) {
        const currentMonthValue = existing.monthValues.get(monthKey) || 0;
        existing.monthValues.set(monthKey, currentMonthValue + item.value);
        existing.total += item.value;
      } else {
        const monthValues = new Map<string, number>();
        monthValues.set(monthKey, item.value);
        divisionMap.set(item.divisionId, {
          divisionId: item.divisionId,
          divisionName: item.divisionName || 'Unknown',
          monthValues,
          total: item.value,
        });
      }
    });

    // Apply cumulative transformation if needed
    if (isCumulative && months.length > 0) {
      divisionMap.forEach((division) => {
        let runningTotal = 0;
        const cumulativeValues = new Map<string, number>();

        // Process months in order (months now includes all months in range)
        // For months without data, the cumulative total stays the same
        months.forEach((month) => {
          const monthValue = division.monthValues.get(month) || 0;
          runningTotal += monthValue;
          cumulativeValues.set(month, runningTotal);
        });

        division.monthValues = cumulativeValues;
        // In cumulative mode, total is the last cumulative value
        if (months.length > 0) {
          const lastMonth = months[months.length - 1];
          division.total = cumulativeValues.get(lastMonth) || 0;
        }
      });
    } else {
      // Recalculate total based on filtered months only (non-cumulative)
      divisionMap.forEach((division) => {
        division.total = months.reduce(
          (sum, month) => sum + (division.monthValues.get(month) || 0),
          0,
        );
      });
    }

    // Convert to array and sort by total descending
    const rows = Array.from(divisionMap.values()).sort(
      (a, b) => b.total - a.total,
    );

    return { months, rows };
  }, [filteredLineItems, getAllMonths, isCumulative]);

  // Sorted division rows
  const sortedDivisionRows = useMemo(() => {
    if (!divisionSortColumn || !volumesByDivision.rows.length) {
      return volumesByDivision.rows;
    }

    const sorted = [...volumesByDivision.rows].sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      if (divisionSortColumn === 'division') {
        aValue = a.divisionName;
        bValue = b.divisionName;
      } else if (divisionSortColumn === 'total') {
        aValue = a.total;
        bValue = b.total;
      } else {
        // It's a month column
        aValue = a.monthValues.get(divisionSortColumn) || 0;
        bValue = b.monthValues.get(divisionSortColumn) || 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return divisionSortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return divisionSortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return sorted;
  }, [volumesByDivision.rows, divisionSortColumn, divisionSortDirection]);

  // Calculate volume breakdown per product group by month
  const volumesByProductGroup = useMemo(() => {
    if (!filteredLineItems) {
      return { months: [], rows: [] };
    }

    const months = getAllMonths(filteredLineItems);

    // Map: productGroupId -> { productGroupName, monthValues: Map<monthKey, value> }
    const productGroupMap = new Map<
      string,
      {
        productGroupId: string | null;
        productGroupName: string;
        monthValues: Map<string, number>;
        total: number;
      }
    >();

    filteredLineItems.forEach((item) => {
      const itemDate = new Date(item.date);
      // Use UTC methods to parse dates consistently (avoid timezone issues)
      const monthKey = `${itemDate.getUTCFullYear()}-${String(itemDate.getUTCMonth() + 1).padStart(2, '0')}`;

      // Only include if month is in our filter
      if (!months.includes(monthKey)) return;

      // Use 'unassigned' as key for items without product group
      const key = item.productGroupId || '__unassigned__';
      const existing = productGroupMap.get(key);

      if (existing) {
        const currentMonthValue = existing.monthValues.get(monthKey) || 0;
        existing.monthValues.set(monthKey, currentMonthValue + item.value);
        existing.total += item.value;
      } else {
        const monthValues = new Map<string, number>();
        monthValues.set(monthKey, item.value);
        productGroupMap.set(key, {
          productGroupId: item.productGroupId,
          productGroupName: item.productGroupName || 'Non-rebateable',
          monthValues,
          total: item.value,
        });
      }
    });

    // Apply cumulative transformation if needed
    if (isCumulative && months.length > 0) {
      productGroupMap.forEach((productGroup) => {
        let runningTotal = 0;
        const cumulativeValues = new Map<string, number>();

        // Process months in order (months now includes all months in range)
        // For months without data, the cumulative total stays the same
        months.forEach((month) => {
          const monthValue = productGroup.monthValues.get(month) || 0;
          runningTotal += monthValue;
          cumulativeValues.set(month, runningTotal);
        });

        productGroup.monthValues = cumulativeValues;
        // In cumulative mode, total is the last cumulative value
        if (months.length > 0) {
          const lastMonth = months[months.length - 1];
          productGroup.total = cumulativeValues.get(lastMonth) || 0;
        }
      });
    } else {
      // Recalculate total based on filtered months only (non-cumulative)
      productGroupMap.forEach((productGroup) => {
        productGroup.total = months.reduce(
          (sum, month) => sum + (productGroup.monthValues.get(month) || 0),
          0,
        );
      });
    }

    // Convert to array and sort by total descending
    const rows = Array.from(productGroupMap.values()).sort(
      (a, b) => b.total - a.total,
    );

    return { months, rows };
  }, [filteredLineItems, getAllMonths, isCumulative]);

  // Sorted product group rows
  const sortedProductGroupRows = useMemo(() => {
    if (!productGroupSortColumn || !volumesByProductGroup.rows.length) {
      return volumesByProductGroup.rows;
    }

    const sorted = [...volumesByProductGroup.rows].sort((a, b) => {
      // Special handling: non-rebateable (null productGroupId) should always be last when sorting by productGroup name
      if (productGroupSortColumn === 'productGroup') {
        const aIsNonRebateable = a.productGroupId === null;
        const bIsNonRebateable = b.productGroupId === null;

        // If both are non-rebateable or both are not, sort normally
        if (aIsNonRebateable === bIsNonRebateable) {
          const comparison = a.productGroupName.localeCompare(
            b.productGroupName,
          );
          return productGroupSortDirection === 'asc' ? comparison : -comparison;
        }

        // One is non-rebateable - it should always be last
        // If ascending, non-rebateable should be last (return 1)
        // If descending, non-rebateable should be last (return 1)
        return aIsNonRebateable ? 1 : -1;
      }

      let aValue: number | string;
      let bValue: number | string;

      if (productGroupSortColumn === 'total') {
        aValue = a.total;
        bValue = b.total;
      } else {
        // It's a month column - always numbers
        aValue = a.monthValues.get(productGroupSortColumn) || 0;
        bValue = b.monthValues.get(productGroupSortColumn) || 0;
      }

      // Both total and month columns are numbers
      return productGroupSortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    return sorted;
  }, [
    volumesByProductGroup.rows,
    productGroupSortColumn,
    productGroupSortDirection,
  ]);

  // Handle division column sort
  const handleDivisionSort = useCallback(
    (column: string) => {
      if (divisionSortColumn === column) {
        // Reverse direction if clicking the same column
        setDivisionSortDirection(
          divisionSortDirection === 'asc' ? 'desc' : 'asc',
        );
      } else {
        // Set new column and default to descending
        setDivisionSortColumn(column);
        setDivisionSortDirection('desc');
      }
    },
    [divisionSortColumn, divisionSortDirection],
  );

  // Handle product group column sort
  const handleProductGroupSort = useCallback(
    (column: string) => {
      if (productGroupSortColumn === column) {
        // Reverse direction if clicking the same column
        setProductGroupSortDirection(
          productGroupSortDirection === 'asc' ? 'desc' : 'asc',
        );
      } else {
        // Set new column and default to descending
        setProductGroupSortColumn(column);
        setProductGroupSortDirection('desc');
      }
    },
    [productGroupSortColumn, productGroupSortDirection],
  );

  // Helper function to format month for CSV header
  const formatMonthForCSV = useCallback((month: string) => {
    const [year, monthNum] = month.split('-');
    const date = new Date(parseInt(year), parseInt(monthNum) - 1);
    const monthName = date.toLocaleDateString('en-US', { month: 'short' });
    return `${monthName} ${year}`;
  }, []);

  // Helper function to download CSV
  const downloadCSV = useCallback(
    (
      filename: string,
      headers: string[],
      rows: (string | number)[][],
      footer?: (string | number)[],
    ) => {
      const csvRows: string[] = [];

      // Add headers
      csvRows.push(headers.map((h) => `"${h}"`).join(','));

      // Add data rows
      rows.forEach((row) => {
        csvRows.push(
          row
            .map((cell) => {
              if (typeof cell === 'number') {
                return cell.toFixed(2);
              }
              return `"${String(cell).replace(/"/g, '""')}"`;
            })
            .join(','),
        );
      });

      // Add footer if provided
      if (footer) {
        csvRows.push(
          footer
            .map((cell) => {
              if (typeof cell === 'number') {
                return cell.toFixed(2);
              }
              return `"${String(cell).replace(/"/g, '""')}"`;
            })
            .join(','),
        );
      }

      // Create blob and download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [],
  );

  // Download handler for division volume table
  const handleDownloadDivisionVolume = useCallback(() => {
    if (!volumesByDivision.rows.length || !volumesByDivision.months.length) {
      return;
    }

    const filename = `Transaction_Volume_by_Division_${new Date().toISOString().split('T')[0]}.csv`;

    // Build headers
    const headers = [
      'Division',
      ...volumesByDivision.months.map(formatMonthForCSV),
      'Total',
    ];

    // Build rows
    const rows = volumesByDivision.rows.map((div) => [
      div.divisionName,
      ...volumesByDivision.months.map(
        (month) => div.monthValues.get(month) || 0,
      ),
      div.total,
    ]);

    // Build footer
    const footer = [
      'Total',
      ...volumesByDivision.months.map((month) =>
        volumesByDivision.rows.reduce(
          (sum, div) => sum + (div.monthValues.get(month) || 0),
          0,
        ),
      ),
      volumesByDivision.rows.reduce((sum, div) => sum + div.total, 0),
    ];

    downloadCSV(filename, headers, rows, footer);
  }, [volumesByDivision, formatMonthForCSV, downloadCSV]);

  // Download handler for product group volume table
  const handleDownloadProductGroupVolume = useCallback(() => {
    if (
      !volumesByProductGroup.rows.length ||
      !volumesByProductGroup.months.length
    ) {
      return;
    }

    const filename = `Transaction_Volume_by_Product_Group_${new Date().toISOString().split('T')[0]}.csv`;

    // Build headers
    const headers = [
      'Product Group',
      ...volumesByProductGroup.months.map(formatMonthForCSV),
      'Total',
    ];

    // Build rows
    const rows = volumesByProductGroup.rows.map((pg) => [
      pg.productGroupName,
      ...volumesByProductGroup.months.map(
        (month) => pg.monthValues.get(month) || 0,
      ),
      pg.total,
    ]);

    // Build footer
    const footer = [
      'Total',
      ...volumesByProductGroup.months.map((month) =>
        volumesByProductGroup.rows.reduce(
          (sum, pg) => sum + (pg.monthValues.get(month) || 0),
          0,
        ),
      ),
      volumesByProductGroup.rows.reduce((sum, pg) => sum + pg.total, 0),
    ];

    downloadCSV(filename, headers, rows, footer);
  }, [volumesByProductGroup, formatMonthForCSV, downloadCSV]);

  // Handler for updating invoice number
  const handleUpdateInvoiceNumber = useCallback(
    async (invoiceId: string, invoiceNumber: string | null) => {
      await updateInvoiceMutation.mutateAsync({
        invoiceId,
        invoiceNumber,
      });
    },
    [updateInvoiceMutation],
  );

  // Invoice columns
  const invoiceColumns: any[] = useMemo(
    () => [
      // Index column (sticky left)
      {
        id: 'index' as const,
        label: '#',
        sortable: false,
        minWidth: '48px',
      },
      // Invoice Number column (editable)
      {
        id: 'invoiceNumber' as const,
        label: 'ID',
        sortable: true,
        render: (_value: unknown, row: RebateInvoice) => (
          <InlineEditableInvoiceNumber
            value={row.invoiceNumber}
            onUpdate={(newValue) => handleUpdateInvoiceNumber(row.id, newValue)}
            isLoading={updateInvoiceMutation.isPending}
          />
        ),
      },

      // Line item date range
      {
        id: 'lineItemDateRange' as const,
        label: 'Line Item Date Range',
        sortable: false,
        render: (
          _value: unknown,
          row: {
            lineItemDateRange?: { earliest: string; latest: string } | null;
          },
        ) => {
          const hasRange =
            row.lineItemDateRange?.earliest && row.lineItemDateRange?.latest;
          if (!hasRange || !row.lineItemDateRange) {
            return <span className="text-sm text-muted-foreground">-</span>;
          }
          const earliestDate = formatPaymentDate(
            row.lineItemDateRange.earliest,
          );
          const latestDate = formatPaymentDate(row.lineItemDateRange.latest);
          const isSameDate =
            row.lineItemDateRange.earliest === row.lineItemDateRange.latest;
          return (
            <span className="text-sm font-medium">
              {isSameDate ? earliestDate : `${earliestDate} - ${latestDate}`}
            </span>
          );
        },
      },
      // Date processed (createdAt)
      {
        id: 'createdAt' as const,
        label: 'Date Processed',
        sortable: true,
        render: (value: string) => (
          <span className="text-sm">{formatPaymentDate(value)}</span>
        ),
      },
      // Rebateable Amount
      {
        id: 'totalRebateableAmount' as const,
        label: 'Rebateable',
        sortable: true,
        align: 'right' as const,
        render: (value: number) => (
          <span className="text-sm font-medium text-green-600">
            {formatCurrency(value)}
          </span>
        ),
      },
      // Total Amount (includes non-rebateable)
      {
        id: 'totalAmount' as const,
        label: 'Total',
        sortable: true,
        align: 'right' as const,
        render: (value: number) => (
          <span className="text-sm">{formatCurrency(value)}</span>
        ),
      },
      // Original file name(s)
      {
        id: 'files' as const,
        label: 'Original File',
        align: 'left' as const,
        render: (
          _v: unknown,
          row: {
            files?: {
              originalFiles?: Array<{ id: string; name: string }>;
              transformedFile?: { id: string; name: string };
            };
          },
        ) => {
          const files = row.files;
          const originals = files?.originalFiles || [];
          if (!originals.length) {
            return <span className="text-sm text-muted-foreground">-</span>;
          }
          const first = originals[0];
          const extra = originals.length - 1;
          return (
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        onViewDocument?.(first.id, first.name);
                      }}
                    >
                      <FileIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View Original File: {first.name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span
                className="text-xs max-w-60 overflow-hidden text-ellipsis whitespace-nowrap"
                title={first.name}
              >
                {first.name}
              </span>
              {extra > 0 && (
                <span className="text-xs text-muted-foreground">
                  +{extra} more
                </span>
              )}
            </div>
          );
        },
      },
      // Transformed file
      {
        id: 'transformedFile' as const,
        label: 'Transformed File',
        align: 'left' as const,
        render: (
          _v: unknown,
          row: {
            files?: {
              transformedFile?: { id: string; name: string } | null;
            };
          },
        ) => {
          const transformed = row.files?.transformedFile;
          if (!transformed) {
            return <span className="text-sm text-muted-foreground">-</span>;
          }
          return (
            <div className="flex items-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        onViewDocument?.(transformed.id, transformed.name);
                      }}
                    >
                      <FileTextIcon className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View Transformed File: {transformed.name}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span
                className="text-xs max-w-60 overflow-hidden text-ellipsis whitespace-nowrap"
                title={transformed.name}
              >
                {transformed.name}
              </span>
            </div>
          );
        },
      },
      // Actions column (only if onDeleteInvoice is provided)
      ...(onDeleteInvoice
        ? [
            {
              id: 'actions' as const,
              label: 'Actions',
              align: 'center' as const,
              minWidth: '80px',
              className: 'whitespace-nowrap',
              render: (_: any, row: any) => (
                <div className="flex gap-2 justify-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            onDeleteInvoice(row.id);
                          }}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Delete Invoice</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              ),
            },
          ]
        : []),
    ],
    [
      handleUpdateInvoiceNumber,
      updateInvoiceMutation.isPending,
      onViewDocument,
      onDeleteInvoice,
    ],
  );

  return (
    <>
      {/* Invoices Table */}
      {!hideInvoicesTable && (
        <>
          {invoicesLoading ? (
            <div className="flex justify-center p-6">
              <CircleNotchIcon className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <GentlyTable
              data={invoices || []}
              columns={invoiceColumns}
              searchFields={['totalAmount']}
              searchPlaceholder="Search invoices..."
              emptyMessage="No invoices found for this agreement"
              showSearch={true}
              showDownload={false}
              summaryRow={{
                index: <span className="text-sm font-bold">Total</span>,
                invoiceNumber: '',
                supplier: '',
                lineItemDateRange: '',
                createdAt: '',
                totalRebateableAmount: (
                  <span className="text-sm font-bold text-green-600">
                    {formatCurrency(
                      invoices?.reduce(
                        (sum, invoice) => sum + invoice.totalRebateableAmount,
                        0,
                      ) || 0,
                    )}
                  </span>
                ),
                totalAmount: (
                  <span className="text-sm font-bold">
                    {formatCurrency(
                      invoices?.reduce(
                        (sum, invoice) => sum + invoice.totalAmount,
                        0,
                      ) || 0,
                    )}
                  </span>
                ),
                files: '',
                transformedFile: '',
                ...(onDeleteInvoice ? { actions: '' } : {}),
              }}
            />
          )}
        </>
      )}

      {/* Non-rebateable toggle - affects both Division and Product Group tables */}
      {lineItems && (
        <div className="mt-6 flex justify-end">
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={showNonRebateable}
              onCheckedChange={setShowNonRebateable}
            />
            <span>Show Non-rebateable</span>
          </label>
        </div>
      )}

      {/* Volume by Division Table */}
      {lineItems && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <FolderIcon className="h-4 w-4 text-primary" />
              Transaction Volume by Division
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadDivisionVolume();
              }}
              disabled={
                !volumesByDivision.rows.length ||
                !volumesByDivision.months.length
              }
              className="gap-2"
            >
              <DownloadIcon className="h-4 w-4" />
              Download CSV
            </Button>
          </div>
          {lineItemsLoading ? (
            <div className="flex justify-center p-6">
              <CircleNotchIcon className="h-6 w-6 animate-spin" />
            </div>
          ) : volumesByDivision.rows.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th
                      className="text-left p-3 text-sm font-medium sticky left-0 bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleDivisionSort('division')}
                    >
                      <div className="flex items-center gap-2">
                        Division
                        {divisionSortColumn === 'division' &&
                          (divisionSortDirection === 'asc' ? (
                            <ArrowUpIcon className="h-3 w-3" />
                          ) : (
                            <ArrowDownIcon className="h-3 w-3" />
                          ))}
                      </div>
                    </th>
                    {volumesByDivision.months.map((month) => {
                      const [year, monthNum] = month.split('-');
                      const date = new Date(
                        parseInt(year),
                        parseInt(monthNum) - 1,
                      );
                      const monthName = date.toLocaleDateString('en-US', {
                        month: 'short',
                      });
                      return (
                        <th
                          key={month}
                          className="text-right p-3 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-muted transition-colors"
                          onClick={() => handleDivisionSort(month)}
                        >
                          <div className="flex items-center justify-end gap-2">
                            {monthName} {year}
                            {divisionSortColumn === month &&
                              (divisionSortDirection === 'asc' ? (
                                <ArrowUpIcon className="h-3 w-3" />
                              ) : (
                                <ArrowDownIcon className="h-3 w-3" />
                              ))}
                          </div>
                        </th>
                      );
                    })}
                    <th
                      className="text-right p-3 text-sm font-medium sticky right-0 bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleDivisionSort('total')}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Total
                        {divisionSortColumn === 'total' &&
                          (divisionSortDirection === 'asc' ? (
                            <ArrowUpIcon className="h-3 w-3" />
                          ) : (
                            <ArrowDownIcon className="h-3 w-3" />
                          ))}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDivisionRows.map((div, index) => (
                    <tr
                      key={div.divisionId}
                      className={
                        index !== sortedDivisionRows.length - 1
                          ? 'border-b'
                          : ''
                      }
                    >
                      <td className="p-3 text-sm sticky left-0 bg-background">
                        {div.divisionName}
                      </td>
                      {volumesByDivision.months.map((month) => (
                        <td
                          key={month}
                          className="p-3 text-sm text-right font-medium"
                        >
                          {formatCurrency(div.monthValues.get(month) || 0)}
                        </td>
                      ))}
                      <td className="p-3 text-sm text-right font-bold sticky right-0 bg-background">
                        {formatCurrency(div.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/50 border-t-2">
                  <tr>
                    <td className="p-3 text-sm font-bold sticky left-0 bg-muted/50">
                      Total
                    </td>
                    {volumesByDivision.months.map((month) => {
                      const monthTotal = volumesByDivision.rows.reduce(
                        (sum, div) => sum + (div.monthValues.get(month) || 0),
                        0,
                      );
                      return (
                        <td
                          key={month}
                          className="p-3 text-sm text-right font-bold"
                        >
                          {formatCurrency(monthTotal)}
                        </td>
                      );
                    })}
                    <td className="p-3 text-sm text-right font-bold sticky right-0 bg-muted/50">
                      {formatCurrency(
                        volumesByDivision.rows.reduce(
                          (sum, div) => sum + div.total,
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="border rounded-lg p-6 text-center text-muted-foreground">
              No data available
            </div>
          )}
        </div>
      )}

      {/* Volume by Product Group Table */}
      {lineItems && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold flex items-center gap-2">
              <FolderIcon className="h-4 w-4 text-primary" />
              Transaction Volume by Product Group
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadProductGroupVolume();
              }}
              disabled={
                !volumesByProductGroup.rows.length ||
                !volumesByProductGroup.months.length
              }
              className="gap-2"
            >
              <DownloadIcon className="h-4 w-4" />
              Download CSV
            </Button>
          </div>
          {lineItemsLoading ? (
            <div className="flex justify-center p-6">
              <CircleNotchIcon className="h-6 w-6 animate-spin" />
            </div>
          ) : volumesByProductGroup.rows.length > 0 ? (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th
                      className="text-left p-3 text-sm font-medium sticky left-0 bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleProductGroupSort('productGroup')}
                    >
                      <div className="flex items-center gap-2">
                        Product Group
                        {productGroupSortColumn === 'productGroup' &&
                          (productGroupSortDirection === 'asc' ? (
                            <ArrowUpIcon className="h-3 w-3" />
                          ) : (
                            <ArrowDownIcon className="h-3 w-3" />
                          ))}
                      </div>
                    </th>
                    {volumesByProductGroup.months.map((month) => {
                      const [year, monthNum] = month.split('-');
                      const date = new Date(
                        parseInt(year),
                        parseInt(monthNum) - 1,
                      );
                      const monthName = date.toLocaleDateString('en-US', {
                        month: 'short',
                      });
                      return (
                        <th
                          key={month}
                          className="text-right p-3 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-muted transition-colors"
                          onClick={() => handleProductGroupSort(month)}
                        >
                          <div className="flex items-center justify-end gap-2">
                            {monthName} {year}
                            {productGroupSortColumn === month &&
                              (productGroupSortDirection === 'asc' ? (
                                <ArrowUpIcon className="h-3 w-3" />
                              ) : (
                                <ArrowDownIcon className="h-3 w-3" />
                              ))}
                          </div>
                        </th>
                      );
                    })}
                    <th
                      className="text-right p-3 text-sm font-medium sticky right-0 bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                      onClick={() => handleProductGroupSort('total')}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Total
                        {productGroupSortColumn === 'total' &&
                          (productGroupSortDirection === 'asc' ? (
                            <ArrowUpIcon className="h-3 w-3" />
                          ) : (
                            <ArrowDownIcon className="h-3 w-3" />
                          ))}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProductGroupRows.map((pg, index) => {
                    const isUnassigned = !pg.productGroupId;
                    return (
                      <tr
                        key={pg.productGroupId || 'unassigned'}
                        className={
                          index !== sortedProductGroupRows.length - 1
                            ? 'border-b'
                            : ''
                        }
                      >
                        <td className="p-3 text-sm sticky left-0 bg-background">
                          {isUnassigned ? (
                            <span className="text-muted-foreground italic">
                              {pg.productGroupName}
                            </span>
                          ) : (
                            pg.productGroupName
                          )}
                        </td>
                        {volumesByProductGroup.months.map((month) => (
                          <td
                            key={month}
                            className="p-3 text-sm text-right font-medium"
                          >
                            {formatCurrency(pg.monthValues.get(month) || 0)}
                          </td>
                        ))}
                        <td className="p-3 text-sm text-right font-bold sticky right-0 bg-background">
                          {formatCurrency(pg.total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-muted/50 border-t-2">
                  <tr>
                    <td className="p-3 text-sm font-bold sticky left-0 bg-muted/50">
                      Total
                    </td>
                    {volumesByProductGroup.months.map((month) => {
                      const monthTotal = volumesByProductGroup.rows.reduce(
                        (sum, pg) => sum + (pg.monthValues.get(month) || 0),
                        0,
                      );
                      return (
                        <td
                          key={month}
                          className="p-3 text-sm text-right font-bold"
                        >
                          {formatCurrency(monthTotal)}
                        </td>
                      );
                    })}
                    <td className="p-3 text-sm text-right font-bold sticky right-0 bg-muted/50">
                      {formatCurrency(
                        volumesByProductGroup.rows.reduce(
                          (sum, pg) => sum + pg.total,
                          0,
                        ),
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="border rounded-lg p-6 text-center text-muted-foreground">
              No data available
            </div>
          )}
        </div>
      )}
    </>
  );
}
